import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import LoginHeader from "../components/LoginHeader.js";
import SideNavigation from "../components/SideNavigation.js";
import AADHeader from "../components/AADHeader.js";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import WestIcon from "@mui/icons-material/West";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { BlobServiceClient } from "@azure/storage-blob";
import { useNavigate, useLocation } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PDFErrorBoundary from "../components/PDFErrorBoundary";
import { collectAllSectionData, loadSectionData } from "./utils/answerManager";
import { generateBRSRPdfBlob } from "./utils/brsrJsPdfGenerator";
import { getSavedReportName } from "../utils/getSavedReportName.js";

const steps = ["Start", "Repository", "Input", "Report"];

/** Prefer first non-empty value (skips null/undefined/"" / empty arrays). */
function pickFirstValue(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && !value.trim()) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    return value;
  }
  return "";
}

/** Display string for legal entity(ies) saved in step1 (array or comma-separated). */
function getLegalEntityDisplayName(step1) {
  if (!step1) return "";
  const le = step1.legalEntity;
  if (Array.isArray(le)) {
    return le
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .join(", ");
  }
  return String(le || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .join(", ");
}

export default function BrsrPreviewReport() {
  const companyid = localStorage.getItem("companyid");
  const organizationValue = localStorage.getItem("organization");
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const reportId = searchParams.get("reportId");
  const isEditMode = !!reportId;

  const savedReportName = getSavedReportName();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [pdffileUrl, setPdfFileUrl] = useState(null);
  const [formData, setFormData] = useState({
    reportTitle: "",
    companyName: "",
    reportingYear: "",
    fileUrl: "",
  });
  const [showPDF, setShowPDF] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [reportData, setReportData] = useState(() => {
    const initialData = JSON.parse(localStorage.getItem("step4")) || [];

    // Check if all objects have an 'id' field
    const allHaveId = initialData.every((item) => item.hasOwnProperty("id"));

    // If all objects have an 'id', sort the array by 'id' in ascending order
    const sortedData = allHaveId ? initialData.sort((a, b) => a.id.localeCompare(b.id)) : initialData;

    // Remove duplicates based on the 'question' field
    const uniqueData = [...new Map(sortedData.map((item) => [item.question.trim(), item])).values()];
    console.log(uniqueData);
    return uniqueData;
  });

  const [fileUploaded, setFileUploaded] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({
      ...formData,
      fileUrl: URL.createObjectURL(file),
    });
    setFileUrl(URL.createObjectURL(file));
    console.log(URL.createObjectURL(file));
    uploadFile("ecoprismfilecontainer", companyid, file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      setFormData({
        ...formData,
        fileUrl: URL.createObjectURL(file),
      });
      setFileUrl(URL.createObjectURL(file));
      uploadFile("ecoprismfilecontainer", companyid, file);
      setFileUploaded(true);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false); // Close sidebar on mobile
      } else {
        setIsSidebarOpen(true); // Open sidebar on larger screens
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // Company name from selected legal entity (step1); reporting year from financial year (step1).
    let dataStep1 = null;
    try {
      const raw = localStorage.getItem("step1");
      if (raw) dataStep1 = JSON.parse(raw);
    } catch {
      dataStep1 = null;
    }
    const entityName = getLegalEntityDisplayName(dataStep1);
    const fy = dataStep1?.financialYear;

    setFormData((prevData) => ({
      ...prevData,
      companyName: entityName || organizationValue || prevData.companyName,
      reportingYear: (fy && String(fy).trim()) || prevData.reportingYear,
    }));
  }, [organizationValue]);

  // Refresh step4 data from section data on mount
  useEffect(() => {
    const storageReportId = reportId || localStorage.getItem("brsr_report_id");
    if (storageReportId) {
      // Collect all section data and refresh step4
      const freshStep4 = collectAllSectionData(storageReportId);
      if (freshStep4 && freshStep4.length > 0) {
        // Sort by id if all entries have id
        const allHaveId = freshStep4.every((item) => item && item.hasOwnProperty("id"));
        const sortedData = allHaveId ? freshStep4.sort((a, b) => (a.id || "").localeCompare(b.id || "")) : freshStep4;
        // Remove duplicates
        const uniqueData = [...new Map(sortedData.map((item) => [item.question?.trim() || item.id, item])).values()];
        setReportData(uniqueData);
      }
    }
  }, [reportId]);

  useEffect(() => {
    return () => {
      if (pdffileUrl) {
        URL.revokeObjectURL(pdffileUrl);
      }
    };
  }, [pdffileUrl]);

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  async function downloadBrsrBlobJson(blobPath) {
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}GetSASToken`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      });
      if (res.status !== 200) return null;
      const responseJson = await res.json();
      const connectionString = responseJson.data;
      const blobServiceClient = new BlobServiceClient(connectionString);
      const containerClient = blobServiceClient.getContainerClient("ecoprismfilecontainer");
      const blobClient = containerClient.getBlobClient(blobPath);
      const downloadBlockBlobResponse = await blobClient.download(0);
      const blob = await downloadBlockBlobResponse.blobBody;
      const downloadedText = await blob.text();
      if (!downloadedText) return null;
      return JSON.parse(downloadedText);
    } catch (error) {
      console.warn("downloadBrsrBlobJson failed:", error);
      return null;
    }
  }

  async function uploadJsonFile(containerName, folderName, fileName, jsonData) {
    try {
      let res = await fetch(`${process.env.REACT_APP_BASE_API_URL}GetSASToken`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      });

      if (res.status === 200) {
        const responseJson = await res.json();
        const connectionString = responseJson.data;

        const blobServiceClient = new BlobServiceClient(connectionString);
        const containerClient = blobServiceClient.getContainerClient(containerName);
        const isBRSRPath = typeof folderName === "string" && folderName.includes("reports/brsr");
        const blobName = isBRSRPath ? folderName : `${folderName}/${fileName}.json`;
        const blobClient = containerClient.getBlobClient(blobName);
        const blockBlobClient = blobClient.getBlockBlobClient();
        const arrayBuffer = new TextEncoder().encode(JSON.stringify(jsonData));
        const uploadResponse = await blockBlobClient.uploadBrowserData(arrayBuffer.buffer, {
          blobHTTPHeaders: { blobContentType: "application/json" },
          blockSize: 4 * 1024 * 1024,
          concurrency: 20,
          onProgress: (ev) => console.log(ev.loadedBytes),
        });
        if (uploadResponse) {
          console.log(`JSON file "${fileName}" uploaded successfully.`);
          console.log(`Blob URL: ${blobClient.url}`);
          localStorage.setItem("bloburl", blobClient.url);
        }
      } else {
        console.error("Failed to fetch SAS token.");
      }
    } catch (error) {
      console.error("Error uploading JSON file:", error);
    }
  }

  async function uploadFile(containerName, folderName, file) {
    try {
      let res = await fetch(`${process.env.REACT_APP_BASE_API_URL}GetSASToken`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      });

      if (res.status === 200) {
        const responseJson = await res.json();
        const connectionString = responseJson.data;

        const blobServiceClient = new BlobServiceClient(connectionString);
        const containerClient = blobServiceClient.getContainerClient(containerName);
        const blobName = `${folderName}/${file.name}`;
        const blobClient = containerClient.getBlobClient(blobName);
        const blockBlobClient = blobClient.getBlockBlobClient();
        const uploadResponse = await blockBlobClient.uploadBrowserData(file, {
          blobHTTPHeaders: { blobContentType: file.type },
          blockSize: 4 * 1024 * 1024,
          concurrency: 20,
          onProgress: (ev) => console.log(ev.loadedBytes),
        });
        if (uploadResponse) {
          console.log(`File "${file.name}" uploaded successfully.`);
          console.log(`Blob URL: ${blobClient.url}`);
          localStorage.setItem("bloburl", blobClient.url);
          localStorage.setItem("logo", file.name);
        }
      } else {
        console.error("Failed to fetch SAS token.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  }

  const validateForm = () => {
    const newErrors = {};
    if (!formData.reportTitle) newErrors.reportTitle = "Report Title is required";
    if (!formData.companyName) newErrors.companyName = "Company Name is required";
    if (!formData.reportingYear) newErrors.reportingYear = "Reporting Year is required";
    if (!formData.fileUrl) newErrors.fileUrl = "Logo is required";

    setErrors(newErrors);
    return newErrors;
  };

  const handleSave = async () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    setLoading(true);

    const step1 = localStorage.getItem("step1");
    const step4 = localStorage.getItem("step4");
    const logo = localStorage.getItem("logo");
    let parseData = null;
    try {
      parseData = step1 ? JSON.parse(step1) : null;
    } catch {
      parseData = null;
    }
    const brsrReportId = reportId || localStorage.getItem("brsr_report_id");

    // Load section data from report-specific storage
    const sectionAData =
      loadSectionData(brsrReportId, "A") ||
      (() => {
        try {
          return JSON.parse(localStorage.getItem("brsr_sectionA_data") || "{}");
        } catch {
          return {};
        }
      })();

    const sectionBData = loadSectionData(brsrReportId, "B") || {};
    const sectionCData = loadSectionData(brsrReportId, "C") || {};

    const generateReport = (() => {
      try {
        return JSON.parse(step4 || "[]");
      } catch {
        return [];
      }
    })();

    const brsrBlobPath = `${companyid}/reports/brsr/${brsrReportId}/data.json`;
    // Merge with existing blob so empty/missing step1 does not permanently wipe Start fields.
    let existingBlob = null;
    try {
      existingBlob = await downloadBrsrBlobJson(brsrBlobPath);
    } catch (mergeErr) {
      console.warn("Unable to load existing BRSR blob before Preview save:", mergeErr);
    }

    const rdata = {
      ...(existingBlob && typeof existingBlob === "object" ? existingBlob : {}),
      reportId: brsrReportId,
      name: pickFirstValue(parseData?.name, existingBlob?.name),
      timeline: pickFirstValue(parseData?.timeline, existingBlob?.timeline),
      timeline1: pickFirstValue(parseData?.timeline1, existingBlob?.timeline1),
      legalEntity: pickFirstValue(parseData?.legalEntity, existingBlob?.legalEntity),
      financialYear: pickFirstValue(parseData?.financialYear, existingBlob?.financialYear),
      reportingOn: pickFirstValue(parseData?.reportingOn, existingBlob?.reportingOn),
      revenuePreviousFY: pickFirstValue(parseData?.revenuePreviousFY, existingBlob?.revenuePreviousFY),
      revenueCurrentFY: pickFirstValue(parseData?.revenueCurrentFY, existingBlob?.revenueCurrentFY),
      sectionA: sectionAData,
      sectionB: sectionBData,
      sectionC: sectionCData,
      generateReport,
      logo: logo || existingBlob?.logo || null,
    };

    await uploadJsonFile("ecoprismfilecontainer", brsrBlobPath, null, rdata);
    // if (selectedFile) {
    //   await uploadFile("ecoprismfilecontainer", companyid, selectedFile);
    // }

    setIsGeneratingPdf(true);
    try {
      const finalReportData = Array.isArray(generateReport) && generateReport.length > 0 ? generateReport : reportData;
      const pdfBlob = await generateBRSRPdfBlob({
        reportData: finalReportData,
        reportTitle: formData.reportTitle,
        reportingYear: formData.reportingYear,
        companyLogo: fileUrl,
      });
      if (pdffileUrl) {
        URL.revokeObjectURL(pdffileUrl);
      }
      const blobUrl = URL.createObjectURL(pdfBlob);
      setPdfFileUrl(blobUrl);
      setShowPDF(true);
    } catch (e) {
      console.error("Failed to generate BRSR PDF:", e);
      setShowPDF(false);
    } finally {
      setIsGeneratingPdf(false);
    }

    setSuccessMessage("Data Updated Successfully");
    setLoading(false);
  };

  const handleDownloadGeneratedPdf = () => {
    if (!pdffileUrl) return;
    const rawTitle = formData.reportTitle || localStorage.getItem("report_name") || "BRSR-Report";
    const safeTitle =
      String(rawTitle)
        .trim()
        .replace(/[\\/:*?"<>|]/g, "-")
        .replace(/\s+/g, " ")
        .slice(0, 120) || "BRSR-Report";
    const a = document.createElement("a");
    a.href = pdffileUrl;
    a.download = `${safeTitle}.pdf`;
    a.click();
  };

  const handleRemoveImage = () => {
    setFileUrl(null); // Assuming you are using useState to manage fileUrl
    setFileUploaded(false);
  };

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {(() => {
        const loginType = localStorage.getItem("loginType");
        if (loginType === "AAD") {
          return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
        }
        return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
      })()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="">
            <div className="row">
              <div className="steps__containt-hader col-md-6">
                <h1 className="title">BRSR</h1>
                <div role="presentation">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/standards">
                      Reports
                    </Link>
                    <Link underline="hover" color="inherit" href="/standards">
                      Standards & Frameworks
                    </Link>
                    <Link underline="hover" color="inherit" href="/brsr/new-report">
                      BRSR
                    </Link>
                    <Typography color="text.primary">{savedReportName}</Typography>
                  </Breadcrumbs>
                </div>
              </div>
              <div className="stepper-box col-md-6">
                <Stepper activeStep={3} alternativeLabel className="brsr-stepper">
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel className="step-name">{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </div>
            </div>
          </div>
          <div>
            <div className="button-box d-flex justify-content-end">
              <Button onClick={() => navigate(isEditMode ? `/brsr/input?reportId=${encodeURIComponent(reportId)}` : "/brsr/input")} variant="outlined" className="module-btn" size="medium" sx={{ marginRight: "15px" }}>
                <WestIcon fontSize="small" sx={{ marginRight: "4px" }} />
                Input
              </Button>
              <Button onClick={() => navigate("/reportDashboard")} variant="outlined" className="module-btn" size="medium" sx={{ marginRight: "15px" }} disabled>
                <img src="/assets/img/reports/Status.svg" style={{ marginRight: "4px" }} alt="Status" />
                Status
              </Button>
              <Button onClick={handleSave} variant="outlined" className="module-btn" size="medium">
                Generate Report
                {/* <EastIcon fontSize="small" sx={{ marginLeft: "4px" }} /> */}
              </Button>
            </div>
            <div className="col-md-12 mt-4 pd-0">
              <form action="">
                <div className="form-row">
                  {successMessage && (
                    <div className="alert alert-success" role="alert">
                      {successMessage}
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="formlable" htmlFor="reportTitle">
                        Report Title
                      </label>
                      <input
                        type="text"
                        required
                        className={`form-control wd-85 ${errors.reportTitle ? "is-invalid" : ""}`}
                        name="reportTitle"
                        placeholder="Enter the report title"
                        value={formData.reportTitle}
                        onChange={(e) => {
                          const input = e.target.value;

                          if (input.length <= 20) {
                            handleFormChange(e);
                          } else {
                            setErrors((prevErrors) => ({
                              ...prevErrors,
                              reportTitle: "Report title cannot exceed 20 characters",
                            }));
                          }
                        }}
                      />
                      {errors.reportTitle && <div className="text-danger">{errors.reportTitle}</div>}
                    </div>
                    <div className="form-group">
                      <label className="formlable" htmlFor="companyName">
                        Company Name
                      </label>
                      <input type="text" required className={`form-control wd-85 ${errors.companyName ? "is-invalid" : ""}`} name="companyName" placeholder="Automatically fetch" value={formData.companyName} onChange={handleFormChange} />
                      {errors.companyName && <div className="text-danger">{errors.companyName}</div>}
                    </div>
                    <div className="form-group">
                      <label className="formlable" htmlFor="reportingYear">
                        Reporting Year
                      </label>
                      <input type="text" required className={`form-control wd-85 ${errors.reportingYear ? "is-invalid" : ""}`} name="reportingYear" placeholder="Automatically fetch" value={formData.reportingYear} onChange={handleFormChange} />
                      {errors.reportingYear && <div className="text-danger">{errors.reportingYear}</div>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="formlable" htmlFor="companyLogo">
                        Company Logo
                      </label>
                      <div className="preview-report-dropzone">
                        <input type="file" id="companyLogo" accept=".jpg,.jpeg,.png" name="fileUrl" style={{ display: "none" }} onChange={handleFileChange} />
                        <label htmlFor="companyLogo" className={`preview-report-upload-box ${fileUrl ? "has-file" : ""}`} onDrop={handleDrop} onDragOver={handleDragOver} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}>
                          {!fileUrl ? (
                            <>
                              <div className="file-icon">
                                <img className="icon" src="/assets/img/import.png" alt="" />
                              </div>
                              <div className="dropzone-title">
                                <span className="browse">Browse</span> or drag and drop
                              </div>
                            </>
                          ) : (
                            <div className="preview-report-file-preview">
                              <img src={fileUrl} alt="Company Logo" />
                              <IconButton
                                aria-label="remove"
                                className="preview-report-remove-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRemoveImage();
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </div>
                          )}
                        </label>
                        <p className="preview-report-upload-hint">Accepted formats: JPG, JPEG, PNG | Max file size: 2 MB</p>
                        {errors.fileUrl && <div className="text-danger">{errors.fileUrl}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="row justify-content-end pt-3">
              <div className="col-auto">
                {!showPDF && (
                  <Button onClick={handleSave} className="next-button" variant="contained" size="medium" sx={{ width: "auto", padding: " 0 30px !important", marginBottom: "30px" }} disabled={isGeneratingPdf}>
                    Preview Report
                  </Button>
                )}
                {showPDF && pdffileUrl && (
                  <PDFErrorBoundary>
                    <Button
                      onClick={handleDownloadGeneratedPdf}
                      className="next-button"
                      variant="contained"
                      size="medium"
                      sx={{
                        width: "auto",
                        padding: " 0 30px !important",
                        marginBottom: "30px",
                        marginLeft: "10px",
                      }}
                    >
                      Download Report
                    </Button>
                  </PDFErrorBoundary>
                )}
              </div>
            </div>
            {showPDF && pdffileUrl && (
              <PDFErrorBoundary>
                <div style={{ width: "100%", height: "100vh" }}>
                  <iframe title="BRSR PDF Preview" src={pdffileUrl} style={{ width: "100%", height: "100%", border: "none" }} />
                </div>
              </PDFErrorBoundary>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
